import { EVComparisonChainModel } from '@/components/organisms/EVComparisonChainModel/EVComparisonChainModel';
import PageHeader from '@/components/organisms/PageHeader';
import { TrimComparisonResult } from '@/components/organisms/TrimComparisonResult';
import { TrimSelectionList } from '@/components/organisms/TrimSelectionList';
import { TrimStyleWithSpecs } from '@/lib/schema/build/types';
import scrollIntoView from '@/lib/scrollIntoView';
import { fetchModelLevelTrims, getAllMakes } from '@/lib/vehicle';
import { EVDetailProps } from '@/types/compare-ev';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface MinimalModel {
  make: string;
  model: string;
  year: string;
}

interface CompareTrimProps extends MinimalModel {
  trims: TrimStyleWithSpecs[];
  makes: string[];
}

const CompareTrims: React.FC<CompareTrimProps> = ({
  year,
  make,
  model,
  trims,
  makes,
}) => {
  const MAX_SELECTABLE_TRIMS = 2;
  const [selectedTrims, setSelectedTrims] = useState<TrimStyleWithSpecs[]>([]);
  const [secondModel, setSecondModel] = useState<MinimalModel>();
  const [secondModelTrims, setSecondModelTrims] = useState<
    TrimStyleWithSpecs[]
  >([]);

  const [modalStep, setModalStep] = useState(0);
  const resultContainerId = '#result';
  const firstTrimSelectionListId = '#first-list';

  const openComparisonModal = () => setModalStep(1);
  const closeComparisonModal = () => setModalStep(-1);

  const scrollToTopList = () =>
    scrollIntoView(
      document.getElementById(firstTrimSelectionListId) as HTMLElement
    );

  const goToNextModalStep = (step: number) => setModalStep(step + 1);

  const addTrim = (newTrim: TrimStyleWithSpecs) => {
    if (
      !selectedTrims.some((trim) => trim.trimId === newTrim.trimId) &&
      selectedTrims.length < MAX_SELECTABLE_TRIMS
    ) {
      setSelectedTrims((prevTrims) => [...prevTrims, newTrim]);
    }
  };

  const removeTrim = (trimId: number) => {
    const updatedTrims = selectedTrims.filter((trim) => trim.trimId !== trimId);
    setSelectedTrims(updatedTrims);
  };

  const trimPreview: EVDetailProps | undefined = useMemo(() => {
    if (!selectedTrims[0]) {
      return undefined;
    }
    return {
      make: selectedTrims[0].make,
      year: String(selectedTrims[0].year),
      model: selectedTrims[0].model,
      trim: selectedTrims[0].trim,
      MSRP: selectedTrims[0].msrp,
      imageURL: selectedTrims[0]?.image ?? '',
    };
  }, [selectedTrims]);

  const onSelectSecondModel = useCallback(
    async (body: { secondEV: EVDetailProps }) => {
      const { secondEV } = body;

      const extendedTrims = await fetchModelLevelTrims({
        make: secondEV.make,
        year: secondEV.year,
        model: secondEV.model,
        fromClient: true,
      });

      setSecondModelTrims(extendedTrims);

      setSecondModel({
        make: secondEV.make,
        model: secondEV.model,
        year: secondEV.year,
      });
      return true;
    },
    []
  );

  useEffect(() => {
    if (selectedTrims.length === 2) {
      scrollIntoView(document.getElementById(resultContainerId) as HTMLElement);
    }
  }, [selectedTrims.length]);

  return (
    <>
      <div className="pt-5 lg:pt-10">
        <PageHeader
          label="Compare Trims"
          className="mb-[20px] px-[20px] lg:px-[40px]"
          headingClassName="!text-h2SemiBold"
          customButton={
            selectedTrims.length === 1 ? (
              <div
                role="button"
                className="ml-4 cursor-pointer border-b-2 border-brandElectric"
                onClick={openComparisonModal}
              >
                <span className="text-body16px font-normal">
                  Compare with another model
                </span>
              </div>
            ) : undefined
          }
        />
        <div>
          <div className="px-5 lg:px-10">
            <div>
              <span className="text-h2Regular text-neutral-600">
                {year} • {make}{' '}
              </span>
            </div>
            <span className="text-h3Regular">{model}</span>
          </div>

          <div
            className={secondModel ? undefined : 'pb-5 lg:pb-10'}
            id={firstTrimSelectionListId}
          >
            <TrimSelectionList
              trims={trims}
              addTrim={addTrim}
              clearTrim={removeTrim}
              selectedTrims={selectedTrims}
              atLimit={selectedTrims.length === MAX_SELECTABLE_TRIMS}
            />
          </div>

          {secondModel && (
            <>
              <div className="p-5 lg:p-10">
                <div>
                  <span className="text-h2Regular text-neutral-600">
                    {secondModel.year} • {secondModel.make}{' '}
                  </span>
                </div>
                <span className="text-h3Regular">{model}</span>
              </div>

              <div className="pb-5 lg:pb-10">
                <TrimSelectionList
                  trims={secondModelTrims}
                  addTrim={addTrim}
                  clearTrim={removeTrim}
                  selectedTrims={selectedTrims}
                  atLimit={selectedTrims.length === MAX_SELECTABLE_TRIMS}
                />
              </div>
            </>
          )}

          {selectedTrims.length > 0 && (
            <TrimComparisonResult
              clearTrim={removeTrim}
              trims={selectedTrims}
              containerId={resultContainerId}
              scrollToTopList={scrollToTopList}
            />
          )}
        </div>
      </div>
      <EVComparisonChainModel
        modalStep={modalStep}
        goToNextModal={goToNextModalStep}
        onClose={closeComparisonModal}
        makes={makes}
        firstChoice={trimPreview}
        onSubmit={onSelectSecondModel}
      />
    </>
  );
};
export default CompareTrims;

export const getStaticProps: GetStaticProps<CompareTrimProps> = async (
  context
) => {
  const make = context.params?.make;
  const year = context.params?.year;
  const model = context.params?.model;

  if (
    !make ||
    !year ||
    !model ||
    typeof make !== 'string' ||
    typeof year !== 'string' ||
    typeof model !== 'string'
  ) {
    return { notFound: true };
  }

  const makes = await getAllMakes();
  const trims = await fetchModelLevelTrims({ make, year, model });

  const sanitizedTrims = trims.map((trim) => ({
    ...trim,
    image: trim.image ?? null,
  }));

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? 'en', ['common'])),
      make,
      year,
      model,
      trims: sanitizedTrims,
      makes,
    },
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
